import { Component, Input, OnInit } from '@angular/core';
import {
  FormGroup,
  FormControl,
  FormArray,
  AbstractControl,
  FormBuilder,
} from '@angular/forms';
import { TechnicalViewService } from 'src/app/_services/technical-view.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { IssuesService } from 'src/app/_services/issues.service';
import { HotkeysService } from 'src/app/_services/hotkeys.service';

@Component({
  selector: 'app-create-technicalrecord',
  templateUrl: './create-technicalrecord.component.html',
  styleUrls: ['./create-technicalrecord.component.scss'],
})
export class CreateTechnicalrecordComponent implements OnInit {
  vehicle_id: any;
  chassis_no: any;
  data: any;
  loading = false;
  record_types: any[] = [
    { name: 'Brakes', value: 'brakes' },
    { name: 'Service', value: 'service' },
    { name: 'Tyres', value: 'tyres' },
    { name: 'Maintenance', value: 'maintenance' },
  ];

  // The dropdown above uses lowercase keys to drive the *ngIf sections in
  // the template, but the established record_type convention already in
  // the database (from the historical data migration) is capitalized and
  // singular for tyres ("Brakes"/"Service"/"Tyre"/"Maintenance"). Sending
  // the raw lowercase key instead created a second, mismatched value
  // ("tyres") that fragments reporting and breaks the edit dropdown's
  // match against legacy "Tyre" records - map to the canonical form here.
  private readonly recordTypeToApi: Record<string, string> = {
    brakes: 'Brakes',
    service: 'Service',
    tyres: 'Tyre',
    maintenance: 'Maintenance',
  };
  selectedRecordType: any;
  @Input() signingOff: boolean = false;
  @Input() isMultipleJobs: boolean = true;

  constructor(
    public technicalViewService: TechnicalViewService,
    private config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private fb: FormBuilder,
    private messageService: MessageService,
    private issuesService: IssuesService,
    private hotkeysService: HotkeysService
  ) {}

  ngOnInit(): void {
    this.data = this.config.data;

    // technicalForm lives on the root-provided TechnicalViewService, so it
    // survives across separate openings of this dialog. Without an explicit
    // reset here, cancelling out of (or even successfully submitting) a
    // previous "Create Technical Record" left odometer/service_jobno/etc.
    // and any linked job entries in place, silently pre-filling the next
    // record opened for a *different* vehicle. onChange() only clears the
    // record-type-specific fields when the dropdown is touched, which
    // isn't enough on its own.
    this.technicalViewService.technicalForm.reset();
    this.technicalJobArray.clear();

    this.technicalViewService.technicalForm.patchValue({
      vehicle_reference: this.data.vehicle_reference,
      vehicle_id: this.data.id,
      chassis_no: this.data.chassis_no,
    });

    this.technicalJobArray.push(
      this.technicalViewService.getTechnicalJobForm()
    );
  }

  onChange($event: any) {
    this.selectedRecordType = $event.value;
    // console.log(this.selectedRecordType);

    // Reset form values based on selected record type. service_jobno was
    // previously missing here, so switching away from "Service" mid-session
    // left a stale job number in the shared form that still got submitted
    // even though its field wasn't visible for the newly selected type.
    this.technicalViewService.technicalForm.patchValue({
      service_brakes: '',
      service_jobno: '',
      remarks: '',
      service_type: '',
      tyre_pos: '',
      tyre_size: '',
      tyre_type: '',
    });

    // Reset form array
    this.technicalJobArray.clear();
    this.addOrder();
  }

  get technicalJobArray() {
    return this.technicalViewService.technicalForm.get(
      'technical_record_jobs'
    ) as FormArray;
  }

  addOrder() {
    const technical_job = this.technicalViewService.getTechnicalJobForm();
    this.technicalJobArray.push(technical_job);
  }

  deleteOrder(index: number) {
    this.technicalJobArray.removeAt(index);
  }

  cleanFormData(formData: any): any {
    if (Array.isArray(formData)) {
      // Clean each element in the array and filter out empty objects
      return formData
        .map((item) => this.cleanFormData(item))
        .filter((item) => Object.keys(item).length > 0);
    } else if (typeof formData === 'object' && formData !== null) {
      const cleanedData: any = {};
      for (const key in formData) {
        if (formData.hasOwnProperty(key)) {
          const value = formData[key];
          if (value !== '' && value !== null) {
            if (typeof value === 'object') {
              // Recursively clean nested objects or arrays
              const cleanedValue = this.cleanFormData(value);
              if (
                Object.keys(cleanedValue).length > 0 ||
                Array.isArray(value)
              ) {
                cleanedData[key] = cleanedValue;
              }
            } else {
              cleanedData[key] = value;
            }
          }
        }
      }
      return cleanedData;
    }
    return formData; // Return primitives as-is
  }

  /** Coerce a job description (string, or a {value,label} suggestion object)
   *  to a plain trimmed string. */
  private normalizeDescription(jd: any): string {
    if (jd == null) return '';
    if (typeof jd === 'string') return jd.trim();
    return String(jd.value ?? jd.label ?? '').trim();
  }

  onSubmit() {
    this.loading = true;

    const formData = this.technicalJobArray.value; // Assuming `form` is your FormGroup

    // Transform `technical_record_jobs` array. A description can arrive as a
    // plain typed string (free-form) or as a {value,label} object (picked from
    // the suggestions) — normalise either to a plain string so free-typed
    // descriptions are never dropped.
    if (formData) {
      const transformedJobs = formData.map((job: any) => {
        return {
          job_description: this.normalizeDescription(job.job_description),
          remarks: job.remarks,
        };
      });

      this.technicalViewService.technicalForm.patchValue({
        technical_record_jobs: transformedJobs,
      });
    }

    const formValue = { ...this.technicalViewService.technicalForm.value };
    if (formValue.record_type) {
      formValue.record_type =
        this.recordTypeToApi[formValue.record_type] ?? formValue.record_type;
    }

    const cleanedData = this.cleanFormData(formValue);
    // console.log(cleanedData);

    this.technicalViewService.createTechnicalRecord(cleanedData).subscribe(
      (res) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Technical record created successfully',
          key: 'technical',
        });
        // Persist any newly-typed component/detail pairs so they appear as
        // suggestions next time (fire-and-forget; never blocks the save).
        (cleanedData.technical_record_jobs || []).forEach((j: any) =>
          this.hotkeysService.ensureShortcut(j.job_description, j.remarks)
        );
        this.ref.close({ res: res, data: cleanedData });
        this.loading = false;
      },
      (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'An error occurred while creating the technical record',
          key: 'technical',
        });
        this.loading = false;
      }
    );
  }
}
